import React, { useEffect, useId, useState } from "react";

import { useTranslation } from "react-i18next";

import { ComboboxBase } from "@/src/components/form-fields/Combobox";
import SortableChipList from "@/src/components/form-fields/SortableChipList";
import { Field, FieldDescription, FieldLabel } from "@/src/components/ui/field";
import { Input } from "@/src/components/ui/input";
import { CastingsTypes } from "@/src/utils/data/Castings";
import { AVAILABLE_METADATA_FILTERS } from "@/src/utils/data/metadata";
import { moveItem } from "@/src/utils/sort";

import type { ChangeEvent, JSX } from "react";

import type { SearchComponentParams } from "@/src/api/ProjectAPI.types";
import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";

type SearchFilters = Omit<SearchComponentParams, "max_results">;

type FilterType = "metadata" | "castings";

interface FilterItem {
  filter: string;
  filterType: FilterType;
}

interface AdvancedSearchProps {
  enabled: boolean;
  filters: SearchFilters;
  maxResults: number;
  onChangeFilters: (filters: SearchFilters) => void;
  onChangeMaxResults: (maxResults: number) => void;
}

const MIN_RESULTS = 0;
const MAX_RESULTS = 10000;
const DRAG_TYPE = "search-filter-chip";

function AdvancedSearch({
  enabled,
  filters,
  maxResults,
  onChangeFilters,
  onChangeMaxResults,
}: AdvancedSearchProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-advancedsearch");
  const maxResultsId = useId();
  const [maxResultsInput, setMaxResultsInput] = useState(String(maxResults));

  useEffect(() => {
    setMaxResultsInput(String(maxResults));
  }, [maxResults]);

  const itemList: FilterItem[] = Object.values(filters)
    .map((filter): FilterItem => {
      const isCasting = filter.field === "castings";
      return {
        filter: isCasting && filter.type ? filter.type : filter.field,
        filterType: isCasting ? "castings" : "metadata",
      };
    })
    .filter(
      (item, index, list) =>
        list.findIndex((other) => other.filter === item.filter) === index,
    );

  const availableList: FilterItem[] = [
    ...AVAILABLE_METADATA_FILTERS.map(
      (filter: string): FilterItem => ({ filter, filterType: "metadata" }),
    ),
    ...CastingsTypes.map(
      (filter: string): FilterItem => ({ filter, filterType: "castings" }),
    ),
  ].filter(
    (available) => !itemList.some((item) => item.filter === available.filter),
  );

  const optionList: ComboboxOption[] = availableList.map((available) => ({
    value: available.filter,
    label: t(available.filter),
  }));

  const refreshFilters = (newItemList: FilterItem[]): void => {
    const newFilters: SearchFilters = {};
    newItemList.forEach((item, index) => {
      newFilters[index] =
        item.filterType === "castings"
          ? {
              field: "castings",
              type: item.filter === "castings" ? undefined : item.filter,
            }
          : { field: item.filter };
    });
    onChangeFilters(newFilters);
  };

  const handleSelect = (value: string): void => {
    const selected = availableList.find(
      (available) => available.filter === value,
    );
    if (selected) {
      refreshFilters([...itemList, selected]);
    }
  };

  const handleReorder = (fromIndex: number, toIndex: number): void => {
    refreshFilters(moveItem(itemList, fromIndex, toIndex));
  };

  const handleRemove = (index: number): void => {
    refreshFilters(itemList.filter((_, itemIndex) => itemIndex !== index));
  };

  const clampResults = (value: number): number =>
    Math.min(Math.max(value, MIN_RESULTS), MAX_RESULTS);

  const handleMaxResults = (event: ChangeEvent<HTMLInputElement>): void => {
    setMaxResultsInput(event.target.value);
    const number = parseInt(event.target.value, 10);
    if (!Number.isNaN(number)) {
      onChangeMaxResults(clampResults(number));
    }
  };

  const handleMaxResultsBlur = (): void => {
    const number = parseInt(maxResultsInput, 10);
    const normalised = Number.isNaN(number)
      ? MIN_RESULTS
      : clampResults(number);
    setMaxResultsInput(String(normalised));
    if (normalised !== maxResults) onChangeMaxResults(normalised);
  };

  return (
    <div className="flex flex-col gap-4">
      <SortableChipList
        disabled={!enabled}
        dragType={DRAG_TYPE}
        onRemove={handleRemove}
        onReorder={handleReorder}
        removeLabel={t("remove")}
        roleDescription={t("sortableItem")}
        items={itemList.map((item) => ({
          id: item.filter,
          label: t(item.filter),
        }))}
      />
      <div className="grid max-w-[720px] grid-cols-1 gap-4 sm:grid-cols-2">
        <ComboboxBase
          disabled={!enabled}
          emptyLabel={t("noResult")}
          label={t("addFilter")}
          onValueChange={handleSelect}
          options={optionList}
          placeholder={t("select")}
          searchPlaceholder={t("search")}
        />
        <Field>
          <FieldLabel htmlFor={maxResultsId}>{t("maxResults")}</FieldLabel>
          <Input
            className="bg-card"
            disabled={!enabled}
            id={maxResultsId}
            max={MAX_RESULTS}
            min={MIN_RESULTS}
            onBlur={handleMaxResultsBlur}
            onChange={handleMaxResults}
            type="number"
            value={maxResultsInput}
          />
          <FieldDescription>
            {t("maxResultsHint", {
              min: MIN_RESULTS.toLocaleString(i18n.language),
              max: MAX_RESULTS.toLocaleString(i18n.language),
            })}
          </FieldDescription>
        </Field>
      </div>
    </div>
  );
}

export default AdvancedSearch;
